import { zodResolver } from '@hookform/resolvers/zod';
import { useTrade } from '@/lib/trade';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  Crown,
  KeyRound,
  Plus,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ShareDialog, type ShareResult } from '@/components/share-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/toast';
import { ApiError, request } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import i18n from '@/lib/i18n';
import { formatRelative } from '@/lib/utils';
import { SeatLimitPrompt, seatLimitOf, SeatsUsedBadge } from './billing/seat-limit-prompt';
import type { SeatLimitDetails } from './billing/billing-api';

/* ---------------------------------------------------------------------------
   הצוות של העסק.

   בלי הזמנות במייל: בעל עסק מוסיף טכנאי בזמן שהוא עומד מולו, ומייל שנופל
   לספאם הוא עובד שלא נכנס. במקום זה — סיסמה ראשונה מוכנה לשליחה בוואטסאפ,
   והעובד מחליף אותה בכניסה.
   --------------------------------------------------------------------------- */

/** הרשאות: אדמין (ניהול החשבון והצוות), עריכה, צפייה בלבד, ועובד שטח. */
const ROLE = {
  OWNER: { labelKey: 'roles.OWNER', hintKey: 'roles.ownerHint' },
  MANAGER: { labelKey: 'roles.MANAGER', hintKey: 'roles.managerHint' },
  VIEWER: { labelKey: 'roles.VIEWER', hintKey: 'roles.viewerHint' },
  FIELD: { labelKey: 'roles.FIELD', hintKey: 'roles.fieldHint' },
} as const;
type Role = keyof typeof ROLE;

/** "טכנאי" לפי התחום: חשמלאי, נגר, עובד ניקיון... */
function useRoleLabel() {
  const trade = useTrade();
  const { t } = useTranslation('settings');
  return (r: Role) => (r === 'FIELD' ? trade.worker.one : t(ROLE[r].labelKey));
}

const memberSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string(),
  role: z.enum(['OWNER', 'MANAGER', 'VIEWER', 'FIELD']),
  skills: z.array(z.string()),
  isActive: z.boolean(),
  mustChangePassword: z.boolean(),
  lastLoginAt: z.string().nullable(),
  /** אימות דו-שלבי דולק — כן/לא בלבד; השרת לא מחזיר שום פרט סודי. */
  mfaEnabled: z.boolean().optional().default(false),
  /** מי שפתח את החשבון — רק הוא משנה אדמינים אחרים (נאכף בשרת). */
  isAccountCreator: z.boolean().optional().default(false),
});
type Member = z.infer<typeof memberSchema>;

const inviteSchema = z.object({
  member: memberSchema,
  temporaryPassword: z.string(),
  loginUrl: z.string(),
  message: z.string(),
  waUrl: z.string().nullable(),
});

/**
 * הרשימה המלאה. מסכים אחרים (בוחר עובד, תהליכים) מושכים גרסה מצומצמת תחת
 * מפתח משלהם (['team', ...]) — מפתח משותף היה מגיש לכאן שורות בלי אימייל.
 * invalidate של ['team'] מרענן את כולם.
 */
const TEAM_KEY = ['team', 'members'];

export function TeamPanel() {
  const { t } = useTranslation('settings');
  const { t: tAuth } = useTranslation('auth');
  const trade = useTrade();
  const roleLabel = useRoleLabel();
  const toast = useToast();
  const qc = useQueryClient();
  const { session } = useAuth();
  const [adding, setAdding] = useState(false);
  const [invite, setInvite] = useState<{ result: ShareResult; name: string } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'reset' | 'remove'; member: Member } | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  /** 409 SEAT_LIMIT משינוי תפקיד / החזרה — הצעת שדרוג מעל הרשימה. */
  const [seatLimit, setSeatLimit] = useState<SeatLimitDetails | null>(null);

  const team = useQuery({
    queryKey: TEAM_KEY,
    queryFn: ({ signal }) => request('/team', { schema: z.array(memberSchema), signal }),
  });

  const showInvite = (r: z.infer<typeof inviteSchema>) =>
    setInvite({
      result: { url: r.loginUrl, waUrl: r.waUrl, message: r.message },
      name: r.member.name,
    });

  const update = useMutation({
    mutationFn: (v: { id: string; data: Partial<Pick<Member, 'role' | 'isActive'>> }) =>
      request(`/team/${v.id}`, { method: 'PATCH', body: v.data, schema: memberSchema }),
    onSuccess: () => {
      setSeatLimit(null);
      void qc.invalidateQueries({ queryKey: ['team'] });
      void qc.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) => {
      const limit = seatLimitOf(e);
      if (limit) {
        setSeatLimit(limit);
        return;
      }
      toast.error(t('team.updateFailed'), e instanceof ApiError ? e.message : undefined);
    },
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) =>
      request(`/team/${id}/reset-password`, { method: 'POST', body: {}, schema: inviteSchema }),
    onSuccess: (r) => {
      setConfirm(null);
      showInvite(r);
      void qc.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (e) =>
      toast.error(t('team.resetFailed'), e instanceof ApiError ? e.message : undefined),
  });

  const me = team.data?.find((m) => m.id === session?.user.id);
  const active = team.data?.filter((m) => m.isActive) ?? [];
  const removed = team.data?.filter((m) => !m.isActive) ?? [];
  /** אדמין אחר (או פותח החשבון) — רק פותח החשבון משנה אותו. השרת אוכף; כאן רק מסתירים. */
  const locked = (m: Member) =>
    m.role === 'OWNER' && m.id !== session?.user.id && !me?.isAccountCreator;

  const renderRow = (m: Member) => (
    <li key={m.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
      {/* בנייד: הפרטים בשורה מלאה, והפעולות מתחתם. */}
      <div className="min-w-0 basis-full sm:basis-0 sm:flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-fg">
          {m.name}
          {m.id === session?.user.id ? (
            <span className="text-xs text-fg-subtle">{t('team.you')}</span>
          ) : null}
          {m.isAccountCreator ? (
            <Badge tone="accent" dot={false}>
              <Crown className="size-3" aria-hidden />
              {t('team.accountCreator')}
            </Badge>
          ) : null}
          {!m.isActive ? <Badge tone="neutral">{t('team.removed')}</Badge> : null}
          {m.mustChangePassword && m.isActive ? (
            <Badge tone="info">{t('team.notLoggedIn')}</Badge>
          ) : null}
          {m.isActive ? (
            <Badge tone={m.mfaEnabled ? 'success' : 'neutral'}>
              {m.mfaEnabled ? tAuth('mfa.team.on') : tAuth('mfa.team.off')}
            </Badge>
          ) : null}
        </p>
        <p className="truncate text-xs text-fg-muted">
          <span className="ltr-inline">
            {m.email.endsWith('@team.local') ? t('team.noEmail') : m.email}
          </span>
          {m.skills.length > 0 ? ` · ${m.skills.join(', ')}` : ''}
          {m.lastLoginAt
            ? ` · ${t('team.lastLogin', { when: formatRelative(m.lastLoginAt) })}`
            : ''}
        </p>
      </div>

      {locked(m) ? (
        <span className="text-xs text-fg-subtle">{roleLabel(m.role)}</span>
      ) : m.isActive ? (
        <>
          <Select
            value={m.role}
            onValueChange={(role) => update.mutate({ id: m.id, data: { role: role as Role } })}
            disabled={update.isPending || m.isAccountCreator}
          >
            <SelectTrigger
              className="h-9 w-40 text-xs"
              aria-label={t('team.roleOf', { name: m.name })}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(ROLE) as Role[]).map((r) => (
                <SelectItem key={r} value={r}>
                  {roleLabel(r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {m.id !== session?.user.id ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirm({ kind: 'reset', member: m })}
              >
                <KeyRound aria-hidden />
                {t('team.resetPassword')}
              </Button>
              {!m.isAccountCreator ? (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirm({ kind: 'remove', member: m })}
                >
                  <UserMinus aria-hidden />
                  {t('team.remove')}
                </Button>
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => update.mutate({ id: m.id, data: { isActive: true } })}
        >
          {t('team.restore')}
        </Button>
      )}
    </li>
  );

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="flex gap-3">
          <div className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-accent-subtle text-accent">
            <Users className="size-4" aria-hidden />
          </div>
          <div>
            <CardTitle>{t('team.title')}</CardTitle>
            <CardDescription>{t('team.description')}</CardDescription>
            <SeatsUsedBadge />
          </div>
        </div>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus aria-hidden />
          {t('team.add')}
        </Button>
      </CardHeader>

      <CardContent>
        {seatLimit ? (
          <SeatLimitPrompt
            details={seatLimit}
            className="mb-3"
            onDismiss={() => setSeatLimit(null)}
          />
        ) : null}
        {team.isLoading ? (
          <div className="space-y-2">
            {[0, 1].map((i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : team.isError ? (
          <ErrorState error={team.error} onRetry={() => void team.refetch()} />
        ) : team.data?.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title={t('team.emptyTitle')}
            description={t('team.emptyDescription', { worker: trade.worker.one })}
          />
        ) : (
          <>
            <ul className="divide-y divide-border">{active.map(renderRow)}</ul>
            {removed.length > 0 ? (
              <div className="mt-3 border-t border-border pt-3">
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs font-medium text-fg-muted hover:text-fg"
                  aria-expanded={showRemoved}
                  onClick={() => setShowRemoved((v) => !v)}
                >
                  <ChevronDown
                    className={`size-3.5 transition-transform ${showRemoved ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                  {t('team.removedTitle', { count: removed.length })}
                </button>
                {showRemoved ? (
                  <ul className="divide-y divide-border">{removed.map(renderRow)}</ul>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </CardContent>

      <RequireMfaToggle />

      {adding ? (
        <AddMemberDialog
          onClose={() => setAdding(false)}
          onAdded={(r) => {
            setAdding(false);
            showInvite(r);
            void qc.invalidateQueries({ queryKey: ['team'] });
            void qc.invalidateQueries({ queryKey: ['billing'] });
          }}
        />
      ) : null}

      {confirm ? (
        <ConfirmMember
          kind={confirm.kind}
          name={confirm.member.name}
          loading={confirm.kind === 'reset' ? resetPassword.isPending : update.isPending}
          onClose={() => setConfirm(null)}
          onConfirm={() =>
            confirm.kind === 'reset'
              ? resetPassword.mutate(confirm.member.id)
              : update.mutate(
                  { id: confirm.member.id, data: { isActive: false } },
                  { onSuccess: () => setConfirm(null) },
                )
          }
        />
      ) : null}

      {invite ? (
        <ShareDialog
          result={invite.result}
          title={t('team.inviteTitle', { name: invite.name })}
          description={t('team.inviteDescription')}
          onClose={() => setInvite(null)}
        />
      ) : null}
    </Card>
  );
}

/** אישור לפני איפוס סיסמה או הסרה — שתיהן מנתקות את העובד מיד. */
function ConfirmMember({
  kind,
  name,
  loading,
  onClose,
  onConfirm,
}: {
  kind: 'reset' | 'remove';
  name: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation(['settings', 'common']);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="confirm-member-desc">
        <DialogHeader>
          <DialogTitle>
            {t(kind === 'reset' ? 'team.resetTitle' : 'team.removeTitle', { name })}
          </DialogTitle>
          <DialogDescription id="confirm-member-desc">
            {t(kind === 'reset' ? 'team.resetDescription' : 'team.removeDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              {t('common:actions.cancel')}
            </Button>
          </DialogClose>
          <Button
            variant={kind === 'remove' ? 'danger' : 'primary'}
            loading={loading}
            onClick={onConfirm}
          >
            {t(kind === 'reset' ? 'team.resetConfirm' : 'team.removeConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const policySchema = z.object({
  requireMfaForManagers: z.boolean(),
  requireMfaForViewers: z.boolean().optional().default(false),
});
type Policy = z.infer<typeof policySchema>;
const POLICY_KEY = ['mfa-policy'];

/** "לדרוש אימות דו-שלבי ממנהלים / מצופים" — הגדרת עסק של הבעלים. נאכף בשרת בכניסה. */
export function RequireMfaToggle() {
  const { t } = useTranslation('auth');
  const toast = useToast();
  const qc = useQueryClient();
  const policy = useQuery({
    queryKey: POLICY_KEY,
    queryFn: ({ signal }) => request('/auth/mfa/policy', { schema: policySchema, signal }),
  });
  const save = useMutation({
    mutationFn: (patch: Partial<Policy>) =>
      request('/auth/mfa/policy', { method: 'PUT', body: patch, schema: policySchema }),
    onSuccess: (data) => {
      qc.setQueryData(POLICY_KEY, data);
      toast.success(t('mfa.team.saved'));
    },
    onError: (e) =>
      toast.error(t('mfa.toast.failed'), e instanceof ApiError ? e.message : undefined),
  });
  const rows = [
    {
      id: 'require-mfa-managers',
      field: 'requireMfaForManagers',
      title: t('mfa.team.requireTitle'),
      description: t('mfa.team.requireDescription'),
    },
    {
      id: 'require-mfa-viewers',
      field: 'requireMfaForViewers',
      title: t('mfa.team.requireViewersTitle'),
      description: t('mfa.team.requireViewersDescription'),
    },
  ] as const;
  return (
    <div className="divide-y divide-border border-t border-border">
      {rows.map((r) => (
        <div key={r.id} className="flex items-start gap-3 px-6 py-4">
          <ShieldCheck className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
          <div className="min-w-0 flex-1">
            <label htmlFor={r.id} className="text-sm font-medium text-fg">
              {r.title}
            </label>
            <p className="text-xs text-fg-muted">{r.description}</p>
          </div>
          <Switch
            id={r.id}
            checked={policy.data?.[r.field] ?? false}
            disabled={policy.isLoading || save.isPending}
            onCheckedChange={(v) => save.mutate({ [r.field]: v })}
          />
        </div>
      ))}
    </div>
  );
}

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, i18n.t('settings:team.nameRequired'))
    .max(120, i18n.t('settings:team.nameTooLong')),
  role: z.enum(['OWNER', 'MANAGER', 'VIEWER', 'FIELD']),
  phone: z.string().trim().max(40, i18n.t('settings:team.phoneTooLong')),
  email: z.string().trim().email(i18n.t('settings:team.invalidEmail')).or(z.literal('')),
  skills: z.string().trim().max(200, i18n.t('settings:team.tooLong')),
});
type FormValues = z.infer<typeof formSchema>;

export function AddMemberDialog({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  onAdded: (r: z.infer<typeof inviteSchema>) => void;
}) {
  const { t } = useTranslation(['settings', 'common']);
  const roleLabel = useRoleLabel();
  const toast = useToast();
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', role: 'FIELD', phone: '', email: '', skills: '' },
  });
  const role = watch('role');
  const [seatLimit, setSeatLimit] = useState<SeatLimitDetails | null>(null);

  const add = useMutation({
    mutationFn: (v: FormValues) =>
      request('/team', {
        method: 'POST',
        body: {
          name: v.name.trim(),
          role: v.role,
          ...(v.email ? { email: v.email.trim().toLowerCase() } : {}),
          ...(v.phone ? { phone: v.phone.trim() } : {}),
          ...(v.skills
            ? {
                skills: v.skills
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .slice(0, 10),
              }
            : {}),
        },
        schema: inviteSchema,
        idempotencyKey: crypto.randomUUID(),
      }),
    onSuccess: onAdded,
    onError: (e) => {
      const limit = seatLimitOf(e);
      if (limit) {
        setSeatLimit(limit);
        return;
      }
      toast.error(t('team.addFailed'), e instanceof ApiError ? e.message : undefined);
    },
  });

  const onSubmit = handleSubmit((v) => add.mutateAsync(v).catch(() => undefined));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="add-member-desc">
        <DialogHeader>
          <DialogTitle>{t('team.addTitle')}</DialogTitle>
          <DialogDescription id="add-member-desc">{t('team.addDescription')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <DialogBody>
            {seatLimit ? <SeatLimitPrompt details={seatLimit} /> : null}
            <Field
              label={t('team.name')}
              htmlFor="member-name"
              error={errors.name?.message}
              required
            >
              <Input
                id="member-name"
                autoFocus
                autoComplete="off"
                invalid={!!errors.name}
                {...register('name')}
              />
            </Field>

            <Field label={t('team.role')} htmlFor="member-role" hint={t(ROLE[role].hintKey)}>
              <Select value={role} onValueChange={(v) => setValue('role', v as Role)}>
                <SelectTrigger id="member-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROLE) as Role[]).map((r) => (
                    <SelectItem key={r} value={r}>
                      {roleLabel(r)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label={t('team.phone')}
              htmlFor="member-phone"
              error={errors.phone?.message}
              hint={t('team.phoneHint')}
            >
              <Input
                id="member-phone"
                type="tel"
                dir="ltr"
                autoComplete="off"
                invalid={!!errors.phone}
                {...register('phone')}
              />
            </Field>

            <Field
              label={t('team.email')}
              htmlFor="member-email"
              error={errors.email?.message}
              hint={t('team.emailHint')}
            >
              <Input
                id="member-email"
                type="email"
                dir="ltr"
                autoComplete="off"
                invalid={!!errors.email}
                {...register('email')}
              />
            </Field>

            {role === 'FIELD' ? (
              <Field
                label={t('team.skills')}
                htmlFor="member-skills"
                error={errors.skills?.message}
                hint={t('team.skillsHint')}
              >
                <Input
                  id="member-skills"
                  autoComplete="off"
                  placeholder={t('team.skillsPlaceholder')}
                  invalid={!!errors.skills}
                  {...register('skills')}
                />
              </Field>
            ) : null}
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                {t('common:actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" loading={add.isPending}>
              {t('common:actions.add')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
